/** How long the site waits for a game to boot — the live preview's and each Tests-panel playground's. A normal boot is well under a second. */
export const LIVE_BOOT_TIMEOUT_MS = 20_000;

/** A boot that did not land within its budget. */
export class BootTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BootTimeoutError';
  }
}

/** A boot given up because the run that wanted it was cancelled. */
export class BootCancelledError extends Error {
  constructor() {
    super('Boot cancelled.');
    this.name = 'BootCancelledError';
  }
}

/**
 * Waits for `booting` — at most `budgetMs`, and no longer than `signal` stays unaborted. A boot that lands in time is
 * handed back. Giving up calls `onGiveUp` — the boot's own abort, so a playground mount frees the page at once — and a
 * boot that lands after all is passed to `dispose`, so it never stays mounted beside the next. A boot's own failure is
 * thrown as itself.
 */
export async function bootWithin<T>(
  booting: Promise<T>,
  options: { budgetMs: number; signal?: AbortSignal; dispose: (late: T) => unknown; onGiveUp?: () => void; timeoutMessage: string },
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;
  const givenUp = new Promise<never>((_, reject) => {
    if (options.signal?.aborted) {
      reject(new BootCancelledError());
      return;
    }
    timer = setTimeout(() => reject(new BootTimeoutError(options.timeoutMessage)), options.budgetMs);
    onAbort = () => reject(new BootCancelledError());
    options.signal?.addEventListener('abort', onAbort, { once: true });
  });
  try {
    return await Promise.race([booting, givenUp]);
  } catch (error) {
    if (error instanceof BootTimeoutError || error instanceof BootCancelledError) {
      options.onGiveUp?.();
      void booting.then((late) => options.dispose(late), () => undefined);
    }
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
    if (onAbort) options.signal?.removeEventListener('abort', onAbort);
  }
}
